import { useContext, useEffect, useMemo, useState } from 'react';

import {
  getAvailableLanguages,
  getCountry,
  getCurrentLocale,
  getLanguage,
  getLanguageOptions
} from '../../../utils/language';
import { PortalConfigContext } from '../../../config/portal';
import {
  SRP_LITERALS,
  useCurrency
} from '../../../pages/SearchResults/components/RefineSearch/FilterHelpers/useSRPFilters';
import {
  boatsUrlGenerate,
  parseSearchParams
} from '../../../utils/urlHelpers/urlPathParse';
import { getFlagHost } from '../../../utils/urlHelpers/location';
import { stateFromUrlMatch } from '../../../utils/urlHelpers/boats';
import { nil } from '../../../utils/runOnceHelper';
import { definedValue } from '../../../utils';

const pickerLanguageValues = (languages, locale) => {
  const currentLanguage = {
    language: locale,
    country: languages[locale].country
  };
  const currentCountryName = getCountry(currentLanguage.country);
  const currentLanguageName = getLanguage(currentLanguage.language);
  const availableLanguages = getAvailableLanguages(languages);
  const languageOptions = getLanguageOptions(availableLanguages, languages);
  return {
    currentLanguage,
    currentCountryName,
    currentLanguageName,
    availableLanguages,
    languageOptions
  };
};

export const useCurrencyPicker = ({
  tpp,
  location,
  pageType,
  match,
  headerPickerMessages
}) => {
  const pathname = definedValue(location?.pathname, '');
  const { languages, supports, host } = useContext(PortalConfigContext);
  const locale = getCurrentLocale();
  const urlParams = useMemo(() => parseSearchParams(pathname), [pathname]);
  const urlState = useMemo(
    () => stateFromUrlMatch(match?.url, match?.params),
    [match]
  );
  const { selectedCurrency: portalCurrency } = useCurrency(urlParams?.currency);
  const [newPathname, setNewPathname] = useState(pathname);
  const [selectedCurrency, setSelectedCurrency] = useState(portalCurrency);
  const [selectedLanguage, setSelectedLanguage] = useState(locale);
  const { currentCountryName, currentLanguageName, languageOptions } = useMemo(
    () => pickerLanguageValues(languages, locale),
    [languages, locale]
  );
  useEffect(() => {
    const changePathName = async () => {
      let changedParams = {};
      const shouldUpdateCurrency = selectedCurrency !== portalCurrency;
      if (shouldUpdateCurrency) {
        changedParams.currency = selectedCurrency;
        const newPath = boatsUrlGenerate(changedParams, urlState, true);
        setNewPathname(newPath);
      }
    };
    changePathName().then(nil).catch(nil);
  }, [selectedCurrency, selectedLanguage, urlParams, portalCurrency, urlState]);

  const onChangeLanguage = (e) => {
    setSelectedLanguage(e.target.value);
  };
  const onChangeCurrency = (e) => {
    setSelectedCurrency(e.target.value);
  };

  const onClickSave = async () => {
    try {
      await tpp.redirectToNewLanguage(selectedLanguage, newPathname);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error saving language/currency:', error);
    }
  };

  // Conditional rendering
  const multiLanguage = supports?.multiLanguage;
  let isUsersCurrencyEnabled = !!supports?.isUsersCurrencyEnabled;
  const isCurrencyDropdownEnabled =
    isUsersCurrencyEnabled &&
    SRP_LITERALS.PAGES_ALLOWED_SHOW_CURRENCY_DROPDOWN.includes(pageType);

  const flagHost = getFlagHost(host, locale);
  return {
    supportsMultiLanguage: multiLanguage,
    languageCurrencyPickerProps: {
      flagCountryUrl: `${flagHost}/assets/icons/flags/${locale}.svg`,
      countryName: currentCountryName,
      languageName: currentLanguageName,
      currencyName: isCurrencyDropdownEnabled
        ? definedValue(selectedCurrency, portalCurrency)
        : '',
      title: headerPickerMessages.settings,
      buttonText: headerPickerMessages.saveAndClose,
      hideCurrency: !isCurrencyDropdownEnabled,
      buttonOnClick: onClickSave,
      items: {
        language: {
          selected: locale,
          pickerType: 'language',
          label: headerPickerMessages.language,
          options: languageOptions,
          onChange: onChangeLanguage
        },
        currency: {
          selected: isCurrencyDropdownEnabled
            ? definedValue(selectedCurrency, portalCurrency)
            : '',
          pickerType: 'currency',
          label: headerPickerMessages.currency,
          options: SRP_LITERALS.CURRENCY_OPTIONS,
          onChange: onChangeCurrency
        }
      }
    }
  };
};
